import React, { useState, useEffect } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from '@mui/material';
import { Search, AdminPanelSettings, ClearAll } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAdminRoutes, setAdminSearchParams, clearAdminError, resetAdminRoutes, clearAllSerialNumbers, setAdminCurrentPage } from '../../store/slices/adminRouteSlice';
import type { RootState } from '../../store';
import { AdminRouteData } from '../../types/adminRoute';
import SerialNumberChips from './SerialNumberChips';

const GetRouteAdmin: React.FC = () => {
  const [shipmentNumber, setShipmentNumber] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const dispatch = useAppDispatch();

  const { routes, loading, error, serialNumbers, pagination } = useAppSelector((state: RootState) => state.adminRoutes);
  const { isAdminAuthenticated, adminApiKey } = useAppSelector((state: RootState) => state.adminAuth);

  // Auto-load recent routes when admin is authenticated
  useEffect(() => {
    if (isAdminAuthenticated && adminApiKey && isInitialLoad) {
      dispatch(fetchAdminRoutes({
        params: {
          shipmentNumber: '', // Empty for initial load
          serialNumbers: undefined,
          page: 0,
          size: 50,
          sortBy: 'id',
          sortDir: 'desc' // Most recent first for initial load
        },
        adminApiKey
      }));
      setIsInitialLoad(false);
    }
  }, [isAdminAuthenticated, adminApiKey, isInitialLoad, dispatch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (shipmentNumber.trim() && adminApiKey) {
      setHasSearched(true);
      const searchParams = {
        shipmentNumber: shipmentNumber.trim(),
        serialNumbers: serialNumbers.length > 0 ? serialNumbers : undefined
      };

      dispatch(setAdminSearchParams(searchParams));
      dispatch(fetchAdminRoutes({
        params: {
          ...searchParams,
          page: 0,
          size: 50,
          sortBy: 'id',
          sortDir: 'asc'
        },
        adminApiKey
      }));
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    const page = value - 1; // Convert from 1-based to 0-based pagination
    dispatch(setAdminCurrentPage(page));

    // Use current search parameters for pagination
    const currentShipmentNumber = hasSearched ? shipmentNumber.trim() : '';
    const currentSerialNumbers = hasSearched ? (serialNumbers.length > 0 ? serialNumbers : undefined) : undefined;
    const sortDirection = hasSearched ? 'asc' : 'desc'; // Recent first for initial load, asc for search

    dispatch(fetchAdminRoutes({
      params: {
        shipmentNumber: currentShipmentNumber,
        serialNumbers: currentSerialNumbers,
        page,
        size: 50,
        sortBy: 'id',
        sortDir: sortDirection
      },
      adminApiKey: adminApiKey!
    }));
  };

  const handleClearAll = () => {
    // Clear all form inputs and results
    setShipmentNumber('');
    setHasSearched(false);
    setIsInitialLoad(true);
    dispatch(clearAllSerialNumbers());
    dispatch(resetAdminRoutes());
    dispatch(clearAdminError());

    // Reload initial data
    if (adminApiKey) {
      dispatch(fetchAdminRoutes({
        params: {
          shipmentNumber: '', // Empty for initial load
          serialNumbers: undefined,
          page: 0,
          size: 50,
          sortBy: 'id',
          sortDir: 'desc' // Most recent first for initial load
        },
        adminApiKey
      }));
      setIsInitialLoad(false);
    }
  };

  const getResultsInfo = () => {
    if (pagination.totalItems === 0) return 'No results';
    const start = pagination.currentPage * pagination.pageSize + 1;
    const end = Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} results`;
  };

  const getResultsTitle = () => (hasSearched ? 'Search Results' : 'Recent Routes');

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettings color="primary" />
          Admin Route Search
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearAdminError())}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
          {/* Shipment Number Input */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Shipment Number (Optional for search)"
              value={shipmentNumber}
              onChange={(e) => setShipmentNumber(e.target.value)}
              disabled={loading}
              placeholder="Leave empty to view recent routes, or enter shipment number to search"
              helperText="Optional field - leave empty to see recent routes"
            />
          </Box>

          {/* Serial Number Chips Component */}
          <SerialNumberChips disabled={loading} />

          {/* Search and Clear Buttons */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {shipmentNumber.trim() ? 'Search Routes' : 'Refresh Recent Routes'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ClearAll />}
              onClick={handleClearAll}
              disabled={loading}
              sx={{ minWidth: 150 }}
              color="secondary"
            >
              Clear All
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Search Results */}
      {routes.length > 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">{getResultsTitle()}</Typography>
            <Typography variant="body2" color="text.secondary">
              {getResultsInfo()}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Shipment Number</TableCell>
                  <TableCell>Route Code</TableCell>
                  <TableCell>Route Description</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>No Override</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routes.map((route: AdminRouteData, index: number) => (
                  <TableRow key={`${route.ShipmentNumber}-${route.SerialNumber}-${index}`}>
                    <TableCell>{route.ShipmentNumber}</TableCell>
                    <TableCell>{route.RouteCode}</TableCell>
                    <TableCell>{route.RouteDescription}</TableCell>
                    <TableCell>{route.SerialNumber}</TableCell>
                    <TableCell>{new Date(route.Date).toLocaleString()}</TableCell>
                    <TableCell>{route.NoOverride ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage + 1} // Convert from 0-based to 1-based
                onChange={handlePageChange}
                disabled={loading}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      )}

      {/* No Results Message */}
      {!loading && routes.length === 0 && hasSearched && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" color="text.secondary" align="center">
            No admin routes found for the specified criteria.
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Try adjusting your search parameters or contact system administrator.
          </Typography>
        </Paper>
      )}

      {/* No Initial Data Message */}
      {!loading && routes.length === 0 && !hasSearched && !isInitialLoad && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" color="text.secondary" align="center">
            No recent routes available.
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Use the search form above to find specific routes.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default GetRouteAdmin;
